import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Heart, MessageCircle, Share2, Send, MoreHorizontal, Smile, Trash2, Flag, Link2, ImageOff, Volume2, VolumeX, Play } from 'lucide-react';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { getMediaUrl, postAPI } from '../services/api';
import { useEmojiPickerPosition } from '../hooks/useEmojiPickerPosition';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const PostCard = ({ post, onLike, onComment, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [postComments, setPostComments] = useState([]);
  const [imageError, setImageError] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [showReelsViewer, setShowReelsViewer] = useState(false);
  const [reelsCommentOpen, setReelsCommentOpen] = useState(false);
  const [reelsCommentText, setReelsCommentText] = useState('');
  const videoRef = useRef(null);
  const reelsVideoRef = useRef(null);
  const postRef = useRef(null);
  const currentUserId = localStorage.getItem('userId');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const { pickerRef, buttonRef, position } = useEmojiPickerPosition(showEmojiPicker);
  
  // Guard against posts with no user data
  if (!post || !post.user) {
    console.warn('PostCard: Skipping post with missing user data', post?._id);
    return null;
  }
  
  // Check ownership - compare with both userId and user.id, handle ObjectId string comparison
  const isOwnPost = String(post.userId) === currentUserId || 
                    String(post.user?.id) === currentUserId ||
                    String(post.user?._id) === currentUserId;
  
  // Initialize comments from post prop
  useEffect(() => {
    const comments = Array.isArray(post.comments) 
      ? post.comments 
      : (Array.isArray(post.commentsData) ? post.commentsData : []);
    setPostComments(comments);
  }, [post.comments, post.commentsData]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (commentText.trim()) {
      const success = await onComment(post._id || post.id, commentText);
      if (success) {
        setCommentText('');
        setShowEmojiPicker(false);
      }
    }
  };

  const handleEmojiClick = (emojiObject) => {
    setCommentText(commentText + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && 
          pickerRef.current && 
          !pickerRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleCommentLike = async (commentId) => {
    try {
      const postId = post._id || post.id;
      const comment = postComments.find(c => (c._id || c.id) === commentId);
      
      if (!comment) return;
      
      // Check if user already liked the comment
      const likes = comment.likes || [];
      const hasLiked = likes.includes(currentUserId);
      
      if (hasLiked) {
        // Unlike the comment
        await postAPI.unlikeComment(postId, commentId);
        
        // Update local state optimistically
        setPostComments(prevComments =>
          prevComments.map(c => {
            if ((c._id || c.id) === commentId) {
              return {
                ...c,
                likes: (c.likes || []).filter(id => id !== currentUserId)
              };
            }
            return c;
          })
        );
      } else {
        // Like the comment
        await postAPI.likeComment(postId, commentId);
        
        // Update local state optimistically
        setPostComments(prevComments =>
          prevComments.map(c => {
            if ((c._id || c.id) === commentId) {
              return {
                ...c,
                likes: [...(c.likes || []), currentUserId]
              };
            }
            return c;
          })
        );
      }
    } catch (error) {
      console.error('Failed to like/unlike comment:', error);
      toast.error('Failed to update like');
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeletePost = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      if (onDelete) {
        await onDelete(post._id || post.id);
      } else {
        toast.error('Delete function not available');
      }
    } catch (error) {
      console.error('Error in handleDeletePost:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleCopyLink = () => {
    const authorUsername = post.user?.username || post.author?.username || '';
    const postUrl = `${window.location.origin}/profile/${authorUsername}`;
    navigator.clipboard.writeText(postUrl)
      .then(() => toast.success('Link copied to clipboard!'))
      .catch(() => toast.info(`Link: ${postUrl}`, { duration: 5000 }));
  };

  const handleReport = () => {
    toast.info('Report functionality coming soon');
  };

  const handleShare = async () => {
    const authorName = post.author?.name || post.user?.name || 'Someone';
    const postContent = post.content ? post.content.substring(0, 100) : 'Check out this post!';
    const postId = post._id || post.id;
    const authorUsername = post.user?.username || post.author?.username || '';
    const shareUrl = `${window.location.origin}/profile/${authorUsername}`;
    const shareText = `${authorName} on SportiZn: "${postContent}${post.content?.length > 100 ? '...' : ''}"`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Post by ${authorName} - SportiZn`,
          text: shareText,
          url: shareUrl
        });
        return;
      }
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      if (error.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        toast.success('Link copied to clipboard!');
      } catch {
        toast.info(`Share this: ${shareUrl}`, { duration: 5000 });
      }
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };

  const getPersonaColor = (persona) => {
    const colors = {
      athlete: '#8854C8',
      coach: '#6B3FA0',
      club: '#9B6DD4',
      nutritionist: '#7545B5',
      physio: '#A47DDB',
      fan: '#8854C8'
    };
    return colors[persona] || '#8854C8';
  };

  // Use mediaUrls from API or images from mock
  const postImages = post.mediaUrls || post.images || [];
  
  const likesCount = Array.isArray(post.likes) ? post.likes.length : (post.likes || 0);
  const commentsCount = postComments.length;

  // Autoplay video when in viewport
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.play().catch(() => {});
          } else {
            videoElement.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (postRef.current) {
      observer.observe(postRef.current);
    }

    return () => {
      if (postRef.current) {
        observer.unobserve(postRef.current);
      }
    };
  }, [currentImageIndex, postImages]);

  // Sync muted state to video elements
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = videoMuted;
    if (reelsVideoRef.current) reelsVideoRef.current.muted = videoMuted;
  }, [videoMuted]);

  // Lock body scroll + manage video playback when reels viewer opens
  useEffect(() => {
    if (showReelsViewer) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) videoRef.current.pause();
      if (reelsVideoRef.current) reelsVideoRef.current.play().catch(() => {});
    } else {
      document.body.style.overflow = '';
      setReelsCommentOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [showReelsViewer]);

  return (
  <>
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl bg-card border-0 mb-4">
      <CardContent className="p-0">
        {/* Post Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center space-x-3">
            <Link to={`/profile/${post.user?.username || 'unknown'}`}>
              <Avatar className="w-10 h-10 cursor-pointer hover:scale-105 transition-transform ring-2 ring-transparent hover:ring-purple-200">
                <AvatarImage src={post.user?.profileImage} alt={post.user?.name || 'User'} />
                <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold">{post.user?.name?.[0] || 'U'}</AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <Link to={`/profile/${post.user?.username || 'unknown'}`} className="hover:opacity-80 transition-opacity">
                <p className="font-semibold text-sm text-foreground leading-tight">{post.user?.name || 'Unknown User'}</p>
              </Link>
              <div className="flex items-center space-x-1.5 mt-0.5">
                <p className="text-xs text-muted-foreground">@{post.user?.username || 'unknown'}</p>
                {post.user?.persona && (
                  <>
                    <span className="text-muted-foreground text-xs">•</span>
                    <Badge 
                      className="text-[10px] px-2 py-0 capitalize font-medium"
                      style={{ 
                        backgroundColor: getPersonaColor(post.user.persona),
                        color: 'white'
                      }}
                    >
                      {post.user.sports && post.user.sports.length > 0 
                        ? `${post.user.sports[0]} ${post.user.persona}` 
                        : post.user.persona}
                    </Badge>
                  </>
                )}
                <span className="text-muted-foreground text-xs">•</span>
                <p className="text-xs text-muted-foreground">{getTimeAgo(post.createdAt || post.timestamp)}</p>
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8 rounded-full">
                <MoreHorizontal className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyLink}>
                <Link2 className="w-4 h-4 mr-2" />
                Copy Link
              </DropdownMenuItem>
              {isOwnPost ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleDeletePost}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Post
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleReport}>
                    <Flag className="w-4 h-4 mr-2" />
                    Report Post
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Post Content */}
        {post.content && (
          <div className="px-5 pb-3">
            <p className="text-foreground text-[15px] leading-relaxed whitespace-pre-wrap">{post.content}</p>
          </div>
        )}

        {/* Post Media (Images/Videos) */}
        {postImages.length > 0 && (
          <div ref={postRef} className="relative bg-black">
            {/* Check if current media is a video */}
            {postImages[currentImageIndex]?.includes('.mp4') || 
             postImages[currentImageIndex]?.includes('.mov') ||
             postImages[currentImageIndex]?.includes('.avi') ||
             postImages[currentImageIndex]?.includes('.webm') ? (
              <>
                {/* Instagram-style video player */}
                <div className="relative cursor-pointer" onClick={() => setShowReelsViewer(true)} data-testid="video-player-container">
                  <video
                    ref={videoRef}
                    src={getMediaUrl(postImages[currentImageIndex])}
                    className="w-full max-h-[600px] object-contain"
                    muted={videoMuted}
                    loop
                    playsInline
                    autoPlay
                  />
                  {/* Mute/Unmute button — bottom right corner */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setVideoMuted(!videoMuted); }}
                    className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white z-10 hover:bg-black/80 transition-colors"
                    data-testid="video-mute-toggle"
                  >
                    {videoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>

              </>
            ) : (
              imageError ? (
                <div className="w-full h-[300px] bg-muted flex flex-col items-center justify-center text-muted-foreground">
                  <ImageOff className="w-12 h-12 mb-2 opacity-50" />
                  <span className="text-sm">Image unavailable</span>
                </div>
              ) : (
                <img
                  src={getMediaUrl(postImages[currentImageIndex])}
                  alt="Post"
                  className="w-full max-h-[600px] object-contain"
                  loading="lazy"
                  decoding="async"
                  onError={() => setImageError(true)}
                />
              )
            )}
            {postImages.length > 1 && (
              <>
                <div className="absolute top-1/2 left-4 right-4 flex justify-between transform -translate-y-1/2">
                  {currentImageIndex > 0 && (
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex - 1)}
                      className="w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-all"
                    >
                      ‹
                    </button>
                  )}
                  {currentImageIndex < postImages.length - 1 && (
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex + 1)}
                      className="w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center ml-auto transition-all"
                    >
                      ›
                    </button>
                  )}
                </div>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {postImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Post Actions */}
        <div className="px-5 py-3">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => onLike(post._id || post.id)}
              className="flex items-center space-x-1.5 group transition-all"
            >
              <Heart
                className={`w-5 h-5 transition-all group-hover:scale-110 ${
                  post.liked ? 'fill-primary text-primary' : 'text-muted-foreground/70 group-hover:text-primary'
                }`}
              />
              <span className={`text-sm ${post.liked ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                {likesCount.toLocaleString()}
              </span>
            </button>
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-1.5 group transition-all"
            >
              <MessageCircle className="w-5 h-5 text-muted-foreground/70 group-hover:text-primary transition-colors group-hover:scale-110" />
              <span className="text-sm text-muted-foreground">{commentsCount}</span>
            </button>
            <button 
              onClick={handleShare}
              className="flex items-center space-x-1.5 group transition-all"
            >
              <Share2 className="w-5 h-5 text-muted-foreground/70 group-hover:text-primary transition-colors group-hover:scale-110" />
              <span className="text-sm text-muted-foreground">Share</span>
            </button>
          </div>

          {/* Comments Section */}
          {showComments && (
            <div className="border-t border-border/50 pt-4 mt-4">
              {/* Comment Input */}
              <form onSubmit={handleCommentSubmit} className="flex items-start space-x-2.5 mb-4">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={currentUser.profileImage} alt={currentUser.name || 'You'} />
                  <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-xs">
                    {currentUser.name?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 flex space-x-2 relative">
                  <div className="flex-1 relative">
                    <Textarea
                      placeholder="Write a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      className="min-h-[60px] resize-none pr-10 text-sm rounded-xl border-border/50"
                    />
                    <button
                      ref={buttonRef}
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="absolute right-2 top-2 p-1 hover:bg-muted rounded-full transition-colors"
                    >
                      <Smile className="w-4 h-4 text-muted-foreground" />
                    </button>
                    {showEmojiPicker && (
                      <div 
                        ref={pickerRef}
                        className="fixed z-50"
                        style={{
                          top: `${position.top}px`,
                          left: `${position.left}px`,
                        }}
                      >
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    size="icon"
                    className="text-white bg-primary hover:bg-[#7C4EE6] rounded-xl h-[60px] w-[60px]"
                    disabled={!commentText.trim()}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-3">
                {postComments.map((comment) => {
                  const commentLikes = comment.likes || [];
                  const commentLikesCount = commentLikes.length;
                  const isCommentLiked = commentLikes.includes(currentUserId);
                  
                  return (
                    <div key={comment._id || comment.id} className="flex items-start space-x-2.5">
                      <Avatar className="w-8 h-8">
                        <AvatarImage 
                          src={comment.user?.profileImage || comment.author?.profileImage || ''} 
                          alt={comment.user?.name || comment.author?.name || 'User'} 
                        />
                        <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-xs">
                          {(comment.user?.name || comment.author?.name || 'U')[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="bg-muted/50 rounded-2xl px-3.5 py-2.5">
                          <p className="font-semibold text-sm text-foreground leading-tight">
                            {comment.user?.name || comment.author?.name || 'User'}
                          </p>
                          <p className="text-sm text-foreground/90 mt-0.5 leading-relaxed">{comment.text}</p>
                        </div>
                        <div className="flex items-center space-x-3 mt-1.5 ml-3">
                          <button 
                            onClick={() => handleCommentLike(comment._id || comment.id)}
                            className={`text-xs transition-colors font-medium flex items-center gap-1 ${
                              isCommentLiked 
                                ? 'text-primary hover:text-purple-600' 
                                : 'text-muted-foreground hover:text-primary'
                            }`}
                          >
                            <Heart className={`w-3 h-3 ${isCommentLiked ? 'fill-current' : ''}`} />
                            {isCommentLiked ? 'Liked' : 'Like'}
                            {commentLikesCount > 0 && (
                              <span className="ml-0.5">({commentLikesCount})</span>
                            )}
                          </button>
                          <span className="text-xs text-muted-foreground">
                            {getTimeAgo(comment.createdAt || comment.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-card rounded-xl p-6 mx-4 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">Delete Post</h3>
            <p className="text-sm text-muted-foreground mb-5">Are you sure you want to delete this post? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2 px-4 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                data-testid="delete-confirm-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>

    {/* Full-screen Reels Viewer — rendered via Portal at document root to avoid clipping */}
    {showReelsViewer && createPortal(
      <div className="fixed inset-0 z-[9999] bg-black" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }} data-testid="reels-viewer-overlay">
        {/* Video — centered, fills viewport */}
        <video
          ref={reelsVideoRef}
          src={getMediaUrl(postImages[currentImageIndex])}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
          loop
          playsInline
          autoPlay
          muted={videoMuted}
        />

        {/* Gradient overlay */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 40%, transparent 100%)', pointerEvents: 'none' }} />

        {/* Top bar — back + mute */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 50, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' }}>
          <button onClick={() => setShowReelsViewer(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'white', background: 'none', border: 'none', cursor: 'pointer', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} data-testid="reels-viewer-back">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Back</span>
          </button>
          <button
            onClick={() => setVideoMuted(!videoMuted)}
            style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', border: 'none', cursor: 'pointer' }}
            data-testid="reels-viewer-mute"
          >
            {videoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>

        {/* Right side — vertical action buttons */}
        <div style={{ position: 'absolute', right: 12, bottom: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, zIndex: 10 }}>
          <button onClick={() => onLike && onLike(post._id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Heart className="w-6 h-6" style={{ color: post.liked ? '#ef4444' : 'white', fill: post.liked ? '#ef4444' : 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            <span style={{ color: 'white', fontSize: 11, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{likesCount}</span>
          </button>
          <button onClick={() => setReelsCommentOpen(!reelsCommentOpen)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
            <MessageCircle className="w-6 h-6" style={{ color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            <span style={{ color: 'white', fontSize: 11, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{commentsCount}</span>
          </button>
          <button style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Share2 className="w-6 h-6" style={{ color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
          </button>
        </div>

        {/* Bottom left — user info + caption */}
        <div style={{ position: 'absolute', bottom: 80, left: 16, right: 64, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Link to={`/profile/${post.user?.username}`} onClick={() => setShowReelsViewer(false)}>
              <Avatar className="w-9 h-9 ring-2 ring-white/30">
                <AvatarImage src={post.user?.profileImage ? getMediaUrl(post.user.profileImage) : undefined} />
                <AvatarFallback className="bg-white/20 text-white text-xs font-semibold">{(post.user?.name || '?')[0]}</AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <Link to={`/profile/${post.user?.username}`} onClick={() => setShowReelsViewer(false)} style={{ textDecoration: 'none' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: 14, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>{post.user?.name}</span>
              </Link>
              {post.user?.persona && (
                <span style={{ marginLeft: 6, padding: '2px 6px', borderRadius: 12, background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 500 }}>{post.user.persona}</span>
              )}
            </div>
          </div>
          {post.content && (
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, lineHeight: 1.4, textShadow: '0 1px 3px rgba(0,0,0,0.6)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>
          )}
        </div>

        {/* Comments panel */}
        {reelsCommentOpen && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 40, maxHeight: '55vh', display: 'flex', flexDirection: 'column', borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden' }} className="bg-card/95 backdrop-blur-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
              <span className="font-semibold text-foreground text-sm">Comments ({commentsCount})</span>
              <button onClick={() => setReelsCommentOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3.5" style={{ maxHeight: '38vh' }}>
              {postComments.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">No comments yet. Be the first!</p>
              ) : (
                postComments.map((c) => (
                  <div key={c._id} className="flex gap-2.5">
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarImage src={c.user?.profileImage} />
                      <AvatarFallback className="text-[10px]">{c.user?.name?.[0] || '?'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <span className="font-semibold text-xs text-foreground">{c.user?.name}</span>
                      <p className="text-sm text-foreground/80 mt-0.5">{c.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex items-center gap-2 p-3 border-t border-border/30">
              <input
                type="text" placeholder="Add a comment..."
                value={reelsCommentText}
                onChange={(e) => setReelsCommentText(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && reelsCommentText.trim() && onComment) {
                    await onComment(post._id, reelsCommentText.trim());
                    setReelsCommentText('');
                  }
                }}
                className="flex-1 bg-muted rounded-full px-3.5 py-2 text-sm outline-none text-foreground placeholder:text-muted-foreground"
              />
              <button
                onClick={async () => {
                  if (reelsCommentText.trim() && onComment) {
                    await onComment(post._id, reelsCommentText.trim());
                    setReelsCommentText('');
                  }
                }}
                disabled={!reelsCommentText.trim()}
                className="text-primary disabled:opacity-30"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>,
      document.body
    )}
  </>
  );
};

export default PostCard;
